import { useState, useEffect } from 'react';
import { establishmentsApi, sectorsApi, adminApi } from '@/lib/api';
import { Building2, Plus, Edit2, Trash2, X, Check, AlertCircle, MapPin, Users, UserCircle, AlertTriangle, Database, ChevronDown, ChevronUp } from 'lucide-react';

const EMPTY_FORM = {
  name: '', sector_id: '', city: '', zone: '',
  size_category: 'moyen', capacity: '', surface_m2: '', opening_hours_week: '',
  employees_fte: '', owner_name: '', owner_email: '',
  // Profil structurel pharmacie
  surface_vente_m2: '', surface_stockage_m2: '',
  robot_delivrance: false, espace_preparation: false,
  lgo: '', annee_installation_titulaire: '',
  // Profil commercial
  nb_medecins_prescripteurs: '', taux_tiers_payant_habituel: '',
  carte_fidelite: false, livraison_domicile: false,
  // Profil financier
  loyer_mensuel_ht: '', stock_ouverture_exercice: '',
};

export default function ClientsPage() {
  const [establishments, setEstablishments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [expandedEst, setExpandedEst] = useState(null);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [deleteAccountConfirm, setDeleteAccountConfirm] = useState(null);

  const load = async () => {
    const [e, s] = await Promise.all([establishmentsApi.list(), sectorsApi.list()]);
    setEstablishments(e.data);
    setSectors(s.data);
    setLoading(false);
  };

  const loadAccounts = async () => {
    try {
      const r = await adminApi.clientAccounts();
      setClientAccounts(r.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); loadAccounts(); }, []);

  const handleSave = async () => {
    if (!form.name.trim() || !form.sector_id) {
      setError('Le nom et le secteur sont obligatoires');
      return;
    }
    setError('');
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => {
        if (['capacity', 'surface_m2', 'opening_hours_week', 'surface_vente_m2', 'surface_stockage_m2', 'loyer_mensuel_ht', 'stock_ouverture_exercice', 'taux_tiers_payant_habituel'].includes(k)) payload[k] = parseFloat(payload[k]) || 0;
        if (k === 'employees_fte') payload[k] = parseFloat(payload[k]) || 0;
        if (k === 'nb_medecins_prescripteurs') payload[k] = parseInt(payload[k]) || 0;
      });
      if (editing) {
        await establishmentsApi.update(editing, payload);
      } else {
        await establishmentsApi.create(payload);
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (est) => {
    setForm({
      name: est.name || '', sector_id: est.sector_id || '',
      city: est.city || '', zone: est.zone || '',
      size_category: est.size_category || 'moyen',
      capacity: est.capacity || '', surface_m2: est.surface_m2 || '',
      opening_hours_week: est.opening_hours_week || '', employees_fte: est.employees_fte || '',
      owner_name: est.owner_name || '', owner_email: est.owner_email || '',
      surface_vente_m2: est.surface_vente_m2 || '', surface_stockage_m2: est.surface_stockage_m2 || '',
      robot_delivrance: est.robot_delivrance || false, espace_preparation: est.espace_preparation || false,
      lgo: est.lgo || '', annee_installation_titulaire: est.annee_installation_titulaire || '',
      nb_medecins_prescripteurs: est.nb_medecins_prescripteurs || '', taux_tiers_payant_habituel: est.taux_tiers_payant_habituel || '',
      carte_fidelite: est.carte_fidelite || false, livraison_domicile: est.livraison_domicile || false,
      loyer_mensuel_ht: est.loyer_mensuel_ht || '', stock_ouverture_exercice: est.stock_ouverture_exercice || '',
    });
    setEditing(est.id);
    setShowForm(true);
  };

  const handleDelete = async (estId) => {
    try {
      const r = await establishmentsApi.delete(estId);
      setDeleteConfirm(null);
      load();
      loadAccounts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleDeleteAccount = async (userId) => {
    try {
      await adminApi.deleteClientAccount(userId);
      setDeleteAccountConfirm(null);
      loadAccounts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur');
    }
  };

  const handlePurge = async () => {
    try {
      const r = await adminApi.purgeAll();
      setPurgeResult(r.data);
      setPurgeConfirm(false);
      load();
      loadAccounts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la purge');
      setPurgeConfirm(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Gestion des clients</h1>
          <p className="text-gray-400 mt-1 text-sm">{establishments.length} etablissement(s) &middot; {clientAccounts.length} compte(s) client</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPurgeConfirm(true)}
            className="bg-red-50 text-red-600 border border-red-200 px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-red-100 transition-all flex items-center gap-2"
            data-testid="purge-all-btn"
          >
            <AlertTriangle size={16} /> Tout supprimer
          </button>
          <button
            onClick={() => { setShowForm(true); setEditing(null); setForm(EMPTY_FORM); }}
            className="bg-[#B8860B] text-white px-5 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#9A7009] transition-all shadow-md flex items-center gap-2"
            data-testid="add-establishment-btn"
          >
            <Plus size={16} /> Nouvel etablissement
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="establishment-error">
          <AlertCircle size={16} /> {error}
          <button onClick={() => setError('')} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Purge result */}
      {purgeResult && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm" data-testid="purge-result">
          Remise a zero terminee : {purgeResult.establishments} etablissement(s), {purgeResult.client_accounts} compte(s), {purgeResult.monthly_data} donnee(s), {purgeResult.reports} rapport(s) supprimes.
          <button onClick={() => setPurgeResult(null)} className="ml-2 underline">Fermer</button>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6" data-testid="establishment-form">
          <h2 className="font-bold text-[#1B2A4A] font-manrope mb-4">{editing ? 'Modifier' : 'Nouvel etablissement'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Nom *</label>
              <input data-testid="est-name-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Pharmacie Pasteur" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Secteur *</label>
              <select data-testid="est-sector-input" value={form.sector_id} onChange={e => setForm({...form, sector_id: e.target.value})} className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]">
                <option value="">Choisir un secteur</option>
                {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Ville</label>
              <input data-testid="est-city-input" value={form.city} onChange={e => setForm({...form, city: e.target.value})} placeholder="Bordeaux" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Zone</label>
              <input data-testid="est-zone-input" value={form.zone} onChange={e => setForm({...form, zone: e.target.value})} placeholder="Bordeaux metropole" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Taille</label>
              <select data-testid="est-size-input" value={form.size_category} onChange={e => setForm({...form, size_category: e.target.value})} className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]">
                <option value="petit">Petit</option>
                <option value="moyen">Moyen</option>
                <option value="grand">Grand</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Places assises</label>
              <input data-testid="est-capacity-input" type="number" value={form.capacity} onChange={e => setForm({...form, capacity: e.target.value})} placeholder="65" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Surface totale (m2)</label>
              <input data-testid="est-surface-input" type="number" value={form.surface_m2} onChange={e => setForm({...form, surface_m2: e.target.value})} placeholder="120" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
              <p className="text-[10px] text-gray-400 mt-1">Surface globale de l'établissement</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Heures/semaine</label>
              <input data-testid="est-hours-input" type="number" value={form.opening_hours_week} onChange={e => setForm({...form, opening_hours_week: e.target.value})} placeholder="50" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Effectif (ETP)</label>
              <input data-testid="est-fte-input" type="number" step="0.1" value={form.employees_fte} onChange={e => setForm({...form, employees_fte: e.target.value})} placeholder="8" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Nom du dirigeant</label>
              <input data-testid="est-owner-input" value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})} placeholder="Pierre Dupont" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Email du dirigeant</label>
              <input data-testid="est-email-input" type="email" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})} placeholder="contact@..." className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
            </div>
          </div>

          {/* Profil structurel pharmacie */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider mb-3">Profil structurel pharmacie</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Surface de vente (m2)</label>
                <input data-testid="est-surface-vente" type="number" value={form.surface_vente_m2} onChange={e => setForm({...form, surface_vente_m2: e.target.value})} placeholder="80" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
                <p className="text-[10px] text-gray-400 mt-1">Espace accessible aux clients</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Surface de stockage (m2)</label>
                <input data-testid="est-surface-stockage" type="number" value={form.surface_stockage_m2} onChange={e => setForm({...form, surface_stockage_m2: e.target.value})} placeholder="40" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
                <p className="text-[10px] text-gray-400 mt-1">Réserve, back-office, préparation</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">LGO utilise</label>
                <select data-testid="est-lgo" value={form.lgo} onChange={e => setForm({...form, lgo: e.target.value})} className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]">
                  <option value="">Choisir</option>
                  <option value="Winpharma">Winpharma</option>
                  <option value="Pharmagest">Pharmagest</option>
                  <option value="LGPI">LGPI</option>
                  <option value="Axios">Axios</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Annee installation titulaire</label>
                <input data-testid="est-annee-titulaire" type="number" value={form.annee_installation_titulaire} onChange={e => setForm({...form, annee_installation_titulaire: e.target.value})} placeholder="2018" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-3">
              {[
                { key: 'robot_delivrance', label: 'Robot de delivrance' },
                { key: 'espace_preparation', label: 'Espace de preparation / officine specialisee' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form[opt.key]} onChange={e => setForm({...form, [opt.key]: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" data-testid={`est-${opt.key}`} />
                  <span className="text-sm text-gray-600">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Profil commercial */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider mb-3">Profil commercial</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Nb medecins prescripteurs (zone)</label>
                <input data-testid="est-nb-medecins" type="number" value={form.nb_medecins_prescripteurs} onChange={e => setForm({...form, nb_medecins_prescripteurs: e.target.value})} placeholder="12" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Taux tiers payant habituel (%)</label>
                <input data-testid="est-taux-tp" type="number" step="0.1" value={form.taux_tiers_payant_habituel} onChange={e => setForm({...form, taux_tiers_payant_habituel: e.target.value})} placeholder="82.5" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-3">
              {[
                { key: 'carte_fidelite', label: 'Carte fidelite' },
                { key: 'livraison_domicile', label: 'Livraison a domicile' },
              ].map(opt => (
                <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form[opt.key]} onChange={e => setForm({...form, [opt.key]: e.target.checked})}
                    className="w-4 h-4 rounded border-gray-300 text-[#1B2A4A] focus:ring-[#1B2A4A]" data-testid={`est-${opt.key}`} />
                  <span className="text-sm text-gray-600">{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Profil financier */}
          <div className="mt-5 pt-4 border-t border-gray-100">
            <p className="text-[10px] font-bold text-[#B8860B] uppercase tracking-wider mb-3">Profil financier de reference</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Loyer mensuel HT</label>
                <input data-testid="est-loyer" type="number" value={form.loyer_mensuel_ht} onChange={e => setForm({...form, loyer_mensuel_ht: e.target.value})} placeholder="3500" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Valeur du stock a l'ouverture de l'exercice</label>
                <input data-testid="est-stock-ouverture" type="number" value={form.stock_ouverture_exercice} onChange={e => setForm({...form, stock_ouverture_exercice: e.target.value})} placeholder="58000" className="w-full h-10 rounded-lg border border-gray-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]" />
                <p className="text-[10px] text-gray-400 mt-1">Valeur du stock le 1er jour de l'exercice comptable</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button onClick={handleSave} className="bg-[#1B2A4A] text-white px-5 py-2 rounded-lg font-semibold text-sm hover:bg-[#152039] flex items-center gap-2" data-testid="est-save-btn">
              <Check size={16} /> {editing ? 'Mettre a jour' : 'Creer'}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="bg-gray-100 text-gray-600 px-5 py-2 rounded-lg font-semibold text-sm hover:bg-gray-200">Annuler</button>
          </div>
        </div>
      )}

      {/* SECTION : Établissements */}
      <div>
        <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope mb-3 flex items-center gap-2">
          <Building2 size={18} /> Etablissements ({establishments.length})
        </h2>
        {establishments.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <Building2 size={40} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Aucun etablissement</p>
            <p className="text-gray-400 text-sm mt-1">La base est vierge. Creez un etablissement ou attendez qu'un client s'inscrive.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {establishments.map(est => (
              <div key={est.id} className="bg-white rounded-xl border border-gray-100 shadow-[0_2px_8px_rgba(27,42,74,0.04)] overflow-hidden" data-testid={`est-card-${est.id}`}>
                <div className="flex items-center px-6 py-4 hover:bg-gray-50/30 transition-all">
                  <div className="w-12 h-12 rounded-lg bg-[#1B2A4A]/5 flex items-center justify-center mr-4 shrink-0">
                    <Building2 size={20} className="text-[#1B2A4A]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-[#1B2A4A] font-manrope">{est.name}</p>
                      {est.is_demo && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">DEMO</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-1 flex-wrap">
                      {est.city && <span className="flex items-center gap-1"><MapPin size={10} /> {est.city}</span>}
                      {est.sector_name && <span className="text-[#B8860B] font-medium">{est.sector_name}</span>}
                      <span className="flex items-center gap-1"><UserCircle size={10} /> {est.client_accounts || 0} compte(s)</span>
                      <span className="flex items-center gap-1"><Database size={10} /> {est.data_count || 0} saisie(s), {est.report_count || 0} rapport(s)</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => setExpandedEst(expandedEst === est.id ? null : est.id)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all" data-testid={`est-expand-${est.id}`}>
                      {expandedEst === est.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    <button onClick={() => handleEdit(est)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1B2A4A] transition-all" data-testid={`est-edit-${est.id}`}>
                      <Edit2 size={16} />
                    </button>
                    <button onClick={() => setDeleteConfirm(est.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all" data-testid={`est-delete-${est.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {/* Expanded details */}
                {expandedEst === est.id && (
                  <div className="px-6 pb-4 pt-1 border-t border-gray-50 bg-gray-50/30">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      {est.zone && <div><span className="text-gray-400">Zone :</span> <span className="text-[#1B2A4A] font-medium">{est.zone}</span></div>}
                      {est.size_category && <div><span className="text-gray-400">Taille :</span> <span className="text-[#1B2A4A] font-medium capitalize">{est.size_category}</span></div>}
                      {est.employees_fte > 0 && <div><span className="text-gray-400">ETP :</span> <span className="text-[#1B2A4A] font-medium">{est.employees_fte}</span></div>}
                      {est.surface_m2 > 0 && <div><span className="text-gray-400">Surface :</span> <span className="text-[#1B2A4A] font-medium">{est.surface_m2} m2</span></div>}
                      {est.owner_name && <div><span className="text-gray-400">Dirigeant :</span> <span className="text-[#1B2A4A] font-medium">{est.owner_name}</span></div>}
                      {est.owner_email && <div><span className="text-gray-400">Email :</span> <span className="text-[#1B2A4A] font-medium">{est.owner_email}</span></div>}
                    </div>
                    {/* Linked client accounts */}
                    {clientAccounts.filter(a => a.establishment_id === est.id).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">Comptes clients lies</p>
                        {clientAccounts.filter(a => a.establishment_id === est.id).map(acc => (
                          <div key={acc.id} className="flex items-center gap-2 text-xs py-1">
                            <UserCircle size={12} className="text-gray-400" />
                            <span className="text-[#1B2A4A] font-medium">{acc.email}</span>
                            <span className="text-gray-400">{acc.plan || 'diagnostic'}</span>
                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${acc.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{acc.status}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SECTION : Comptes clients orphelins (sans établissement valide) */}
      {clientAccounts.filter(a => !establishments.find(e => e.id === a.establishment_id)).length > 0 && (
        <div>
          <h2 className="text-lg font-bold text-[#1B2A4A] font-manrope mb-3 flex items-center gap-2">
            <UserCircle size={18} /> Comptes clients orphelins
          </h2>
          <div className="bg-white rounded-xl border border-gray-100 shadow-[0_2px_8px_rgba(27,42,74,0.04)] divide-y divide-gray-50">
            {clientAccounts.filter(a => !establishments.find(e => e.id === a.establishment_id)).map(acc => (
              <div key={acc.id} className="flex items-center px-6 py-3" data-testid={`orphan-${acc.id}`}>
                <UserCircle size={18} className="text-amber-500 mr-3" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1B2A4A]">{acc.name || acc.email}</p>
                  <p className="text-xs text-gray-400">{acc.email} &middot; Plan : {acc.plan || 'diagnostic'} &middot; {acc.status}</p>
                </div>
                <button onClick={() => setDeleteAccountConfirm(acc.id)} className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-all" data-testid={`del-account-${acc.id}`}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal : Confirmer suppression d'un établissement */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => setDeleteConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-red-500" />
              <h2 className="text-lg font-bold text-[#1B2A4A]">Confirmer la suppression</h2>
            </div>
            <p className="text-sm text-gray-600 mb-1">
              Cela supprimera definitivement cet etablissement ainsi que :
            </p>
            <ul className="text-sm text-gray-500 list-disc ml-5 mb-4 space-y-0.5">
              <li>Toutes les donnees mensuelles associees</li>
              <li>Tous les rapports generes</li>
              <li>Tous les comptes clients rattaches</li>
              <li>Tous les tickets/flux commerciaux</li>
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 h-10 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="flex-1 h-10 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-all" data-testid="confirm-delete-btn">
                Supprimer definitivement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal : Confirmer suppression d'un compte client */}
      {deleteAccountConfirm && (
        <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => setDeleteAccountConfirm(null)}>
          <div className="bg-white rounded-xl shadow-2xl border border-gray-100 p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="delete-account-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={20} className="text-red-500" />
              <h2 className="text-lg font-bold text-[#1B2A4A]">Supprimer ce compte</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">Le compte client sera definitivement supprime.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteAccountConfirm(null)} className="flex-1 h-10 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">Annuler</button>
              <button onClick={() => handleDeleteAccount(deleteAccountConfirm)} className="flex-1 h-10 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700" data-testid="confirm-delete-account-btn">
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal : Confirmer purge totale */}
      {purgeConfirm && (
        <div className="fixed inset-0 bg-black/30 z-[60] flex items-center justify-center p-4" onClick={() => setPurgeConfirm(false)}>
          <div className="bg-white rounded-xl shadow-2xl border border-red-200 p-6 w-full max-w-md" onClick={e => e.stopPropagation()} data-testid="purge-confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={24} className="text-red-500" />
              <h2 className="text-lg font-bold text-red-700">Remise a zero totale</h2>
            </div>
            <div className="bg-red-50 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-700 font-semibold mb-1">Action irreversible !</p>
              <p className="text-sm text-red-600">Cela supprimera :</p>
              <ul className="text-sm text-red-600 list-disc ml-5 mt-1 space-y-0.5">
                <li>Tous les etablissements ({establishments.length})</li>
                <li>Tous les comptes clients ({clientAccounts.length})</li>
                <li>Toutes les donnees mensuelles et rapports</li>
                <li>Tous les tickets et contacts</li>
              </ul>
              <p className="text-sm text-red-700 font-semibold mt-2">Votre compte admin, les secteurs et les KPIs seront conserves.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPurgeConfirm(false)} className="flex-1 h-10 bg-gray-100 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-200">Annuler</button>
              <button onClick={handlePurge} className="flex-1 h-10 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-all" data-testid="confirm-purge-btn">
                Confirmer la purge totale
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
